import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Button, Dialog, DialogActions, DialogContent, Typography } from '@mui/material'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common'
import { JoiValidationOptions, Mode } from 'constants/index'
import {
  CreateAndUpdateStrategicPartnerContactDtoInput,
  useCreateStrategicPartnerContactMutation,
  useUpdateStrategicPartnerContactMutation,
} from 'generated/graphql'
import { SuppliersContactValidation } from 'validation-schemas'

import ContactForm from './ContactForm'
import UpdateContact from './UpdateContact'

interface IProps {
  contactId: number | null
  strategicPartnerId: number
  openDialog: boolean
  handleCloseDialog: () => any
}

const CreateUpdateContact = (props: IProps) => {
  const { contactId, strategicPartnerId, openDialog, handleCloseDialog } = props

  const methods = useForm<CreateAndUpdateStrategicPartnerContactDtoInput>({
    shouldUnregister: true,
    resolver: joiResolver(SuppliersContactValidation, JoiValidationOptions),
    defaultValues: {
      name: '',
      isDefault: false,
      telephoneNumber: '',
      email: '',
      additionalEmails: '',
      department: '',
      jobRole: '',
      strategicPartnerId,
    },
  })

  const { handleSubmit } = methods

  // Mutations
  const [createContact, { loading: creatingContact }] = useCreateStrategicPartnerContactMutation({
    onCompleted: (data) => {
      toast.success(`Contact ${data?.createStrategicPartnerContact.name} was successfully created.`)
      handleCloseDialog()
    },
  })

  const [updateContact, { loading: updatingContact }] = useUpdateStrategicPartnerContactMutation({
    onCompleted: (data) => {
      toast.success(`Contact ${data?.updateStrategicPartnerContact.name} was successfully updated.`)
      handleCloseDialog()
    },
  })

  const mode = contactId ? Mode.Update : Mode.Create

  const handleSubmitForm: SubmitHandler<CreateAndUpdateStrategicPartnerContactDtoInput> = (
    formData,
  ) => {
    if (mode === Mode.Create) {
      createContact({
        variables: {
          input: { ...formData, strategicPartnerId, isDefault: false },
        },
      })
    } else {
      updateContact({
        variables: {
          input: {
            ...formData,
            id: contactId,
            strategicPartnerId,
            isDefault: false,
          },
        },
      })
    }
  }

  const handleOnCancel = () => {
    // ToDo: check if form is dirty
    handleCloseDialog()
  }

  const isProcessing = creatingContact || updatingContact

  return (
    <Dialog
      open={openDialog}
      fullWidth
      maxWidth='md'
      aria-labelledby='strategic-partner-contact-dialog-title'>
      <FplDialogTitle id='strategic-partner-contact-dialog-title' onClose={handleOnCancel}>
        <Typography paragraph variant='h4'>
          {mode === Mode.Create ? 'New Contact' : 'Update Contact'}
        </Typography>
      </FplDialogTitle>

      <DialogContent>
        <FormProvider {...methods}>
          {mode === Mode.Create ? <ContactForm /> : <UpdateContact contactId={contactId!} />}
        </FormProvider>
      </DialogContent>

      <DialogActions>
        <Button variant='contained' color='grey' onClick={handleOnCancel}>
          Cancel
        </Button>

        <LoadingButton
          variant='contained'
          loading={isProcessing}
          onClick={handleSubmit(handleSubmitForm)}>
          Save
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default CreateUpdateContact
